import { useTour } from '@/contexts/TourContext';
import { tours } from '@/config/tours';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  HelpCircle, 
  Play, 
  CheckCircle2, 
  Clock, 
  BookOpen, 
  MessageCircle,
  Keyboard 
} from 'lucide-react';
import { useEffect } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface HelpMenuProps {
  onNavigate: (path: string) => void;
}

export function HelpMenu({ onNavigate }: HelpMenuProps) {
  const { startTour, restartTour, getTourState, setPendingTour } = useTour();

  const handleTourClick = (tourId: string) => {
    // Special handling for timesheet tour - navigate to timesheet page first
    if (tourId === 'timesheet-tour') {
      setPendingTour(tourId);
      onNavigate('/timesheet');
      return;
    }

    // For other tours, start immediately
    const state = getTourState(tourId);
    if (state?.status === 'completed') {
      restartTour(tourId);
    } else {
      startTour(tourId);
    }
  };

  // Global keyboard shortcut: Press "?" to open help
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Check if user is typing in an input field
      const target = e.target as HTMLElement;
      const isInputField = 
        target.tagName === 'INPUT' || 
        target.tagName === 'TEXTAREA' || 
        target.isContentEditable;

      // Only trigger if not in an input field and no modifiers are pressed
      if (e.key === '?' && !isInputField && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        // Trigger the dropdown menu
        const helpButton = document.querySelector('[data-tour="help-menu"]') as HTMLButtonElement;
        if (helpButton) {
          helpButton.click();
        }
      }
    };

    window.addEventListener('keypress', handleKeyPress);
    return () => window.removeEventListener('keypress', handleKeyPress);
  }, []);

  return (
    <TooltipProvider>
      <Tooltip>
        <DropdownMenu>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" data-tour="help-menu">
                <HelpCircle className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <DropdownMenuContent align="end" className="w-[320px]">
            <DropdownMenuLabel className="flex items-center gap-2">
              <HelpCircle className="h-4 w-4" />
              Help & Support
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            
            {/* Product Tours Section */}
            <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground px-2 py-1.5">
              Product Tours
            </DropdownMenuLabel>
            
            {tours.map((tour) => {
              const state = getTourState(tour.id);
              const isCompleted = state?.status === 'completed';
              const isInProgress = state?.status === 'in_progress';

              return (
                <DropdownMenuItem
                  key={tour.id}
                  onClick={() => handleTourClick(tour.id)}
                  className="flex items-start gap-3 py-3 cursor-pointer"
                >
                  <div className="shrink-0 mt-0.5">
                    {isCompleted ? (
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    ) : isInProgress ? (
                      <Play className="h-4 w-4 text-primary" />
                    ) : (
                      <Play className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm">{tour.name}</span>
                      {isCompleted && (
                        <Badge variant="secondary" className="text-xs">
                          Completed
                        </Badge>
                      )}
                      {isInProgress && (
                        <Badge variant="default" className="text-xs">
                          In Progress
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {tour.description}
                    </p>
                    {tour.estimatedDuration && (
                      <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        <span>{tour.estimatedDuration}s</span>
                      </div>
                    )}
                  </div>
                </DropdownMenuItem>
              );
            })}

            <DropdownMenuSeparator />

            {/* Documentation & Support Section */}
            <DropdownMenuItem 
              onClick={() => onNavigate('/help/docs')}
              className="flex items-center gap-3 py-2.5 cursor-pointer"
            >
              <BookOpen className="h-4 w-4" />
              <div className="flex-1">
                <div className="font-medium text-sm">Documentation</div>
                <div className="text-xs text-muted-foreground">User guides and resources</div>
              </div>
            </DropdownMenuItem>

            <DropdownMenuItem 
              onClick={() => onNavigate('/help/support')}
              className="flex items-center gap-3 py-2.5 cursor-pointer"
            >
              <MessageCircle className="h-4 w-4" />
              <div className="flex-1">
                <div className="font-medium text-sm">Contact Support</div>
                <div className="text-xs text-muted-foreground">Get help from our team</div>
              </div>
            </DropdownMenuItem>

            <DropdownMenuSeparator />
            
            <DropdownMenuItem className="text-xs text-muted-foreground justify-center" disabled>
              <Keyboard className="h-3 w-3 mr-1.5" />
              Press <kbd className="mx-1 px-1.5 py-0.5 bg-muted rounded text-xs font-mono">?</kbd> for quick access
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <TooltipContent>
          <p>Help & Support - Press ? for quick access</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
